/**
 * Providers: who the field providers are, whether they can sign in, whether
 * their buildings are covered and open to them, and the route planned for
 * today. Everything here is read from history eMerge already records
 * (docs/record-flow/providers.md); nothing is guessed. The route is the PLANNED
 * route and the check-ins that happened, never where a provider is now.
 * No resident appears anywhere on this tab.
 */
import React from 'react';
import { ProviderRow, ProviderStop, Providers, WindowParams, flowApi } from './flowApi';
import { ChartCard, EmptyNote, Pill, Remote, fmtN, useRemote } from './flowUi';
import { MetricTile } from '../../../components/tide';

const ACTIVATION_WORDS: Record<string, string> = {
  PENDING_INVITE: 'invited, not accepted yet',
  OTP_ISSUED: 'one-time code issued',
  PASSWORD_SET: 'password set, setup not finished',
};

const STOP_STATE_WORDS: Record<ProviderStop['state'], string> = { PLANNED: 'planned', CONFIRMED: 'confirmed', COMPLETED: 'completed' };

const activationWords = (a: string | null) => (a ? (ACTIVATION_WORDS[a] || a.replace(/_/g, ' ').toLowerCase()) : null);

const LOGIN_STATE_WORDS: Record<string, string> = {
  DEACTIVATED: 'deactivated', SUSPENDED: 'suspended', DELETED: 'deleted', NOT_WORKFORCE: 'not a workforce account',
};

/** "Oct 19, 10:30 AM EDT": date, time and zone (America/New_York), with the year when it is not this year. */
export function fmtSeen(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const tz = 'America/New_York';
  const year = (x: Date) => new Intl.DateTimeFormat('en-US', { timeZone: tz, year: 'numeric' }).format(x);
  return new Intl.DateTimeFormat('en-US', {
    timeZone: tz, month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
    ...(year(d) !== year(now) ? { year: 'numeric' } : {}),
  }).format(d);
}

export default function ProvidersView({ window: w, onSelectPerson }: {
  window: WindowParams; onSelectPerson: (userId: string, name: string) => void;
}) {
  const { state, retry } = useRemote<Providers>(`providers|${w.from}|${w.to}`, () => flowApi.providers(w));
  return <Remote state={state} retry={retry} skeleton="chart">{(data) => <ProvidersBody data={data} onSelectPerson={onSelectPerson} />}</Remote>;
}

function ProvidersBody({ data, onSelectPerson }: { data: Providers; onSelectPerson: (userId: string, name: string) => void }) {
  const { totals, providers } = data;
  const needing = providers.filter((p) => p.attention.length > 0);
  const withStops = providers.filter((p) => p.today.stops.length > 0);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="providers-tiles">
        <MetricTile label="Providers" value={fmtN(totals.providers)} />
        <MetricTile label="Need attention" value={fmtN(totals.needAttention)} caveat="at least one flag below" />
        <MetricTile label="Not signed in yet" value={fmtN(totals.notSignedInYet)} caveat="linked login, never signed in (unusable logins are flagged apart)" />
        <MetricTile label="Stops today" value={fmtN(totals.stopsToday)} caveat={data.today ? `planned for ${data.today}` : undefined} />
      </div>
      {totals.manifestLinesNoProvider > 0 && (
        <p className="tide-chart__foot" data-testid="providers-no-provider-caveat">
          {fmtN(totals.manifestLinesNoProvider)} manifest {totals.manifestLinesNoProvider === 1 ? 'line' : 'lines'} in this window name no provider, so they are not counted for anyone.
        </p>
      )}

      <section className="tide-panel" aria-labelledby="providers-attention-title">
        <header className="tide-panel__head">
          <h2 className="tide-panel__title" id="providers-attention-title">Needs attention</h2>
          <p className="tide-panel__hint">Each flag says what is wrong, in words, and names the buildings it is about. These are facts about now, not about the window.</p>
        </header>
        {needing.length === 0 ? (
          <EmptyNote>No provider has an attention flag right now.</EmptyNote>
        ) : (
          <ul className="space-y-3" data-testid="providers-attention">
            {needing.map((p) => (
              <li key={p.employeeId} data-testid="attention-row">
                <strong>{p.name}</strong>
                <ul className="mt-1 space-y-1">
                  {p.attention.map((a) => (
                    <li key={a.code} className="flex flex-wrap items-center gap-2">
                      <Pill tone="danger">{a.words}</Pill>
                      {a.facilities.length > 0 && (
                        <span className="tide-tiny tide-ink-2">{a.facilities.map((f) => f.name || 'Unnamed building').join(', ')}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="tide-panel" aria-labelledby="providers-table-title">
        <header className="tide-panel__head">
          <h2 className="tide-panel__title" id="providers-table-title">Providers</h2>
          <p className="tide-panel__hint">One row per provider. Coverage and sign-in are now; visits, check-ins and manifest lines are for the window above.</p>
        </header>
        {providers.length === 0 ? (
          <EmptyNote>No providers were found.</EmptyNote>
        ) : (
          <div className="tide-scroll" tabIndex={0} role="region" aria-label="Providers (scrolls sideways)">
            <table className="tide-table min-w-[1180px]">
              <thead>
                <tr>
                  <th scope="col">Provider</th>
                  <th scope="col">Sign-in</th>
                  <th scope="col">Last seen</th>
                  <th scope="col">Login link</th>
                  <th scope="col">Coverage (buildings / closed / access missing)</th>
                  <th scope="col">Visits dated in window</th>
                  <th scope="col" className="tide-num">Check-ins</th>
                  <th scope="col">Manifest lines (decided / undecided)</th>
                  <th scope="col">Open visits (overdue)</th>
                  <th scope="col">Last action</th>
                </tr>
              </thead>
              <tbody>
                {providers.map((p) => <ProviderTableRow key={p.employeeId} p={p} onSelect={onSelectPerson} />)}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="tide-panel" aria-labelledby="providers-route-title">
        <header className="tide-panel__head">
          <h2 className="tide-panel__title" id="providers-route-title">Today&apos;s route</h2>
          <p className="tide-panel__hint">Planned route only. eMerge records where a provider was planned to go and where they checked in, never where they are now.</p>
        </header>
        {withStops.length === 0 ? (
          <p data-testid="providers-no-route">No visits planned today.</p>
        ) : (
          <div className="space-y-4">
            {withStops.map((p) => (
              <div key={p.employeeId} data-testid="route-provider">
                <h3 className="font-semibold">{p.name}</h3>
                <ol className="mt-1 space-y-2">
                  {[...p.today.stops].sort((a, b) => a.position - b.position).map((s) => <StopItem key={s.planId} s={s} />)}
                </ol>
              </div>
            ))}
          </div>
        )}
      </section>

      <ChartCard
        exposeChildren
        title="Not recorded"
        hint="What eMerge does not hold, so nothing on this tab pretends to know it."
        summary={`Not recorded: ${data.notRecorded.map((n) => n.words).join(' ') || 'nothing listed'}`}
      >
        {data.notRecorded.length === 0 ? (
          <p>Nothing listed.</p>
        ) : (
          <ul className="list-disc space-y-1 pl-5" data-testid="providers-not-recorded">
            {data.notRecorded.map((n) => <li key={n.code}>{n.words}</li>)}
          </ul>
        )}
      </ChartCard>
    </div>
  );
}

function signInCell(p: ProviderRow) {
  const { login } = p;
  if (login.signIn === 'NOT_LINKED') return 'No login linked';
  if (login.signIn === 'LOGIN_UNUSABLE') return <>Login cannot be used<span className="tide-tiny tide-ink-2 block">{LOGIN_STATE_WORDS[login.state] || login.state.toLowerCase()}</span></>;
  if (login.signIn === 'NOT_SIGNED_IN_YET') {
    const act = activationWords(login.activationState);
    return <>Not signed in yet{act && <span className="tide-tiny tide-ink-2 block">{act}</span>}</>;
  }
  return 'Has signed in';
}

function linkCell(link: ProviderRow['login']['link']) {
  if (link === 'MANAGER_LINKED') return 'linked by a manager';
  if (link === 'PRE_RULING_LINK') return 'linked before the 2026-10-19 ruling (not ratified)';
  return '-';
}

function ProviderTableRow({ p, onSelect }: { p: ProviderRow; onSelect: (userId: string, name: string) => void }) {
  const { login, coverage, window: win, open } = p;
  return (
    <tr>
      <td>
        {login.userId ? (
          <button type="button" className="tide-link" onClick={() => onSelect(login.userId as string, p.name)} aria-label={`Show steps by ${p.name}`}>{p.name}</button>
        ) : p.name}
      </td>
      <td>{signInCell(p)}</td>
      <td className="tide-tiny">{!login.linked ? 'No login linked' : login.lastSeenAt ? fmtSeen(login.lastSeenAt) : 'never'}</td>
      <td className="tide-tiny">{linkCell(login.link)}</td>
      <td className="tabular-nums">{fmtN(coverage.buildings)} / {fmtN(coverage.closed)} / {fmtN(coverage.accessMissing)}</td>
      <td className="tabular-nums">
        {fmtN(win.visitsPlanned)} not cancelled
        <span className="tide-tiny tide-ink-2 block">of which {fmtN(win.visitsCompleted)} completed; {fmtN(win.visitsCancelled)} cancelled besides</span>
      </td>
      <td className="tide-num tabular-nums">{fmtN(win.checkIns)}</td>
      <td className="tabular-nums">{fmtN(win.manifestDecided)} / {fmtN(win.manifestUndecided)}</td>
      <td className="tabular-nums">{fmtN(open.visits)} ({fmtN(open.overdue)} overdue)</td>
      <td className="tide-tiny">
        {p.lastActionAt
          ? `${fmtSeen(p.lastActionAt)}${p.lastActionSource ? ` (${p.lastActionSource === 'CHECK_IN' ? 'check-in' : 'Record Flow step'})` : ''}`
          : 'none recorded'}
      </td>
    </tr>
  );
}

function StopItem({ s }: { s: ProviderStop }) {
  return (
    <li data-testid="route-stop" data-position={s.position} className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <strong>{s.position}.</strong>
      <span>{s.facilityName || 'Unnamed building'}</span>
      {s.addressLine && <span className="tide-tiny tide-ink-2">{s.addressLine}</span>}
      <span className="tide-tiny">{s.arrivalTime ? `arrives ${s.arrivalTime}` : 'no time set'}</span>
      <Pill tone="neutral">{STOP_STATE_WORDS[s.state] || s.state}</Pill>
      {s.buildingClosed && <Pill tone="danger">closed for providers</Pill>}
      <span className="tide-tiny">{s.checkedInAt ? `checked in ${fmtSeen(s.checkedInAt)}` : 'not checked in'}</span>
      {s.mapUrl && <a href={s.mapUrl} target="_blank" rel="noopener noreferrer" className="tide-link">Map (opens in new tab)</a>}
    </li>
  );
}
